import {Plural, t, Trans} from '@lingui/macro';
import {isEqual, sortBy} from 'lodash';
import React, {FC, useMemo, useReducer, useState} from 'react';
import styled from 'styled-components';

import {TextAreaBox} from '../../../libs/shared/ui/src/components/boxedInputs/textAreaBox';
import {TextBox} from '../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {useLocalize} from '../../../srcCommon/internationalization/internationalizationContext';
import {OkCancelDialog} from '../../components/common/dialogs/okCancelDialog';
import {useAppSelector} from '../../helpers/reduxHelpers';
import {ApiCircle, CircleModel, serializeCircle, UnsavedCircleModel} from '../../models/api/circleModel';
import {SettingsResourcePagesEnum} from '../../models/url/settingsUrlModel';
import {deleteAsync, postApiAsync, putApiAsync} from '../../services/http/http';
import {getCompany} from '../../store/data/company/companySelectors';
import {SettingsFieldSection} from '../common/settingsFieldSection';
import {
  SettingsFooter,
  StyledSettingsContent,
  StyledSettingsMain,
  StyledSettingsWrapper,
} from '../common/settingsWrapper';
import {useInvalidateCircle} from '../fetchers/circles';
import {settingsHorizontalSpacing} from '../settingsCommonConstants';
import {CircleEditorHeader} from './circleEditorHeader';
import {CircleEditorTeammatesPicker} from './circleEditorTeammatesPicker';
import {useCircleNavigateToLink} from './circlesHelpers';

/*
 * Types.
 */

type CircleState = UnsavedCircleModel;

/*
 * Props.
 */

interface CircleEditorProps {
  circle?: CircleModel;
}

/*
 * Styles.
 */

export const StyledCircleEditorContent = styled.div`
  padding-left: ${settingsHorizontalSpacing}px;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 500px;
  max-width: 756px;
  width: 100%;
`;

const StyledInputWrap = styled.div`
  max-width: 302px;
  margin-bottom: 20px;
`;

const StyledSettingsFieldSection = styled(SettingsFieldSection)`
  height: 100%;
`;

const StyledGroupMembersTitleDiv = styled.div`
  display: flex;
  align-items: baseline;
`;

const StyledMemberCountDiv = styled.span`
  font-size: ${fontSizes.veryTiny};
  font-weight: ${fontWeights.normal};
  margin-left: 8px;
`;

const StyledRequiredSpan = styled.span`
  color: ${(p) => p.theme.palette.red.shade40};
  margin-left: 3px;
`;

/*
 * Component.
 */

export const CircleEditor: FC<CircleEditorProps> = (props) => {
  const {circle} = props;
  const isEditing = circle !== undefined;

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const navigateTo = useCircleNavigateToLink();
  const [circleState, dispatch] = useReducer(circleEditorReducer, circle ?? defaultState);
  const company = useAppSelector(getCompany);

  const invalidateCircle = useInvalidateCircle();

  const handleSubmit = async () => {
    const serializedCircle = serializeCircle(circleState);
    if (circle) {
      await putApiAsync(ApiCircle, circle.url, serializedCircle);
      invalidateCircle(circle.id, {shouldUncache: true});
    } else {
      await postApiAsync(ApiCircle, `${company.url}/namespaces/global/circles`, serializedCircle);
    }

    navigateTo(SettingsResourcePagesEnum.OVERVIEW);
  };

  const handleDelete = async () => {
    if (!circle) {
      return;
    }

    await deleteAsync(circle.url);
    navigateTo(SettingsResourcePagesEnum.OVERVIEW);
  };

  const onDelete = () => {
    setShowDeleteDialog(true);
  };

  // TODO: Refactor all delete dialogs in the settings.
  const maybeRenderDeleteDialog = () => {
    if (!showDeleteDialog) {
      return null;
    }

    return (
      <OkCancelDialog
        title={<Trans id="circle-editor-delete-dialog-title">Delete {circle?.name}</Trans>}
        okButtonLabel={<Trans id="circle-editor-delete-dialog-ok">Delete permanently</Trans>}
        onOkClick={handleDelete}
        onRequestClose={() => setShowDeleteDialog(false)}
        okButtonVariant={ButtonVariantsEnum.DANGER}
      >
        <Trans id="circle-editor-delete-dialog-message">
          This group will be permanently deleted in Front.
          <br />
          This cannot be undone.
        </Trans>
      </OkCancelDialog>
    );
  };

  const isSubmitDisabled = useIsSubmitButtonDisabled(circle, circleState);

  const localize = useLocalize();

  return (
    <StyledSettingsWrapper>
      <CircleEditorHeader isEditing={isEditing} onDelete={onDelete} />
      {maybeRenderDeleteDialog()}
      <StyledSettingsMain>
        <StyledSettingsContent>
          <StyledCircleEditorContent>
            <StyledInputWrap>
              <TextBox
                value={circleState.name}
                onChange={(newName) => dispatch({type: CircleActionsEnum.SET_NAME, payload: newName})}
                size={VisualSizesEnum.MEDIUM}
                label={<Trans id="settings-circle-editor-label-name">Name</Trans>}
                shouldFocus
                isRequired
                placeholder={localize(
                  t('settings-circle-editor-placeholder-name')`For example: “Support” or “Managers”`,
                )}
              />
            </StyledInputWrap>
            <StyledInputWrap>
              <TextAreaBox
                value={circleState.description}
                onChange={(newDescription) =>
                  dispatch({type: CircleActionsEnum.SET_DESCRIPTION, payload: newDescription})
                }
                label={<Trans id="settings-circle-editor-label-description">Description</Trans>}
                placeholder={localize(
                  t('settings-circle-editor-placeholder-description')`Describe the group’s purpose`,
                )}
              />
            </StyledInputWrap>
            <StyledSettingsFieldSection
              title={
                <StyledGroupMembersTitleDiv>
                  <Trans id="settings-circle-editor-label-members">Group members</Trans>
                  <StyledRequiredSpan>*</StyledRequiredSpan>
                  <StyledMemberCountDiv>
                    <Plural
                      id="settings-circle-editor-label-members-count"
                      value={circleState.teammateIds.length}
                      one="# member"
                      other="# members"
                    />
                  </StyledMemberCountDiv>
                </StyledGroupMembersTitleDiv>
              }
            >
              <CircleEditorTeammatesPicker
                teammateIds={circleState.teammateIds}
                onChange={(teammateIds) =>
                  dispatch({type: CircleActionsEnum.SET_TEAMMATES, payload: teammateIds})
                }
                circleId={circle?.id}
              />
            </StyledSettingsFieldSection>
          </StyledCircleEditorContent>
        </StyledSettingsContent>
      </StyledSettingsMain>
      <SettingsFooter>
        <Button
          variant={ButtonVariantsEnum.SECONDARY}
          onClick={() => navigateTo(SettingsResourcePagesEnum.OVERVIEW)}
        >
          <Trans id="settings-circles-editor-cancel">Cancel</Trans>
        </Button>
        <Button variant={ButtonVariantsEnum.PRIMARY} onClick={handleSubmit} isDisabled={isSubmitDisabled}>
          {isEditing ? (
            <Trans id="settings-circles-editor-save">Update group</Trans>
          ) : (
            <Trans id="settings-circles-editor-create">Create group</Trans>
          )}
        </Button>
      </SettingsFooter>
    </StyledSettingsWrapper>
  );
};

/*
 * Helpers.
 */

const defaultState: CircleState = {
  name: '',
  description: '',
  teammateIds: [],
  namespace: 'global',
};

enum CircleActionsEnum {
  SET_NAME = 'set_name',
  SET_DESCRIPTION = 'set_description',
  SET_TEAMMATES = 'set_teammates',
}

interface SetNameAction {
  type: CircleActionsEnum.SET_NAME;
  payload: string;
}

interface SetDescriptionAction {
  type: CircleActionsEnum.SET_DESCRIPTION;
  payload: string;
}

interface SetTeammatesAction {
  type: CircleActionsEnum.SET_TEAMMATES;
  payload: readonly number[];
}

type CircleAction = SetNameAction | SetTeammatesAction | SetDescriptionAction;

function circleEditorReducer(state: CircleState, action: CircleAction): CircleState {
  switch (action.type) {
    case CircleActionsEnum.SET_NAME:
      return {
        ...state,
        name: action.payload,
      };

    case CircleActionsEnum.SET_DESCRIPTION:
      return {
        ...state,
        description: action.payload,
      };

    case CircleActionsEnum.SET_TEAMMATES:
      return {
        ...state,
        teammateIds: action.payload,
      };

    default:
      return state;
  }
}

function useIsCircleValid(state: CircleState) {
  if (state.name.trim() === '') {
    return false;
  }

  return state.teammateIds.length > 0;
}

function useIsCircleUpdated(circle: CircleModel | undefined, state: CircleState) {
  return useMemo(() => {
    if (!circle) {
      return true;
    }

    if (circle.name.trim() !== state.name.trim()) {
      return true;
    }

    if (circle.description !== state.description) {
      return true;
    }

    return !isEqual(sortBy(circle.teammateIds), sortBy(state.teammateIds));
  }, [circle, state.description, state.name, state.teammateIds]);
}

function useIsSubmitButtonDisabled(circle: CircleModel | undefined, state: CircleState) {
  const isValid = useIsCircleValid(state);
  const isUpdated = useIsCircleUpdated(circle, state);

  return !isValid || !isUpdated;
}
